import type { ErrorBody } from '@swf/core/errors/RequestError';

interface Response {
  success?: boolean;
  message?: string;
}

interface Props<T> {
  response: Response & T;
  accessor: string;
}

interface ReturnType<R> {
  error?: string | null;
  message?: string | null;
  data: R & {
    errCode: number;
    errDesc?: string;
  };
}

export const handleServerResponse = <T, R>({ response, accessor }: Props<T>): ReturnType<R> => {
  const success = response?.success;
  const message = response?.message;
  const data = response?.[accessor];

  if (!data && message) {
    return { error: message, data };
  }

  if (!data) {
    throw new Error(
      `A key with the value of ${accessor} was not found in ${JSON.stringify(response)}`
    );
  }

  // Server error or empty param error
  if (!success) {
    return { error: message, data };
  }

  // If there was not server error we search for a client error.
  // errCode above 0 is treated as error.
  if (success && data.errCode > 0) {
    return { error: data.errDesc, data };
  }

  return { error: null, message, data };
};

// TODO: decide on error handling and iterate to implement it
export const handleServerError = ({ text }: ErrorBody): string => text;
